import React, { lazy, memo, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { Box, Chip, useMediaQuery, Grid, Badge, IconButton, Typography } from '@mui/material';
import '../../../components/pages.css';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import MapsUgcOutlinedIcon from '@mui/icons-material/MapsUgcOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { createSelector } from 'reselect';
import { getModuleQuickLaunchDataAPI, pendingTaskAPI } from '../../../Action.jsx';
import { exportToExcel, getZoneColor } from '../../../Utility/Utility.jsx';
import { setRedZones } from '../../../Slice/userSlice.js';
import {
  setMyTaskGridData,
  setPriority,
  setSftwrModules,
  setZone
} from '../../../Slice/myTaskSlice.js';
import theme from '../../../theme.js';
import {
  AnimatedSearchField,
  ChipLevel,
  CmpButton,
  CmpTypographyHead,
  EllipsisTooltip
} from '../../../commonComponents/Toobox.js';
import { DataGridPro } from '@mui/x-data-grid-pro';
import {
  createMultiSelectOperator,
  zoneOperator
} from '../../../commonComponents/DataGridFilterComp.jsx';
import CmpModal from '../../../commonComponents/CmpModal.jsx';

// Lazy load components
const TaskModal = lazy(() => import('./TaskModal.jsx'));
const UpdateFollowUpDrawer = lazy(() =>
  import('../../../commonComponents/UpdateFollowUpDrawer.jsx')
);

const badgeStyle = {
  '& .MuiBadge-badge': {
    border: `2px solid ${theme.palette.primary.text}`,
    top: '8% !important',
    right: '12px !important',
    height: '21px !important'
  }
};
// Memoize components to avoid re-renders
const UpdateFollowUpDrawerMemo = memo(UpdateFollowUpDrawer);
const TaskModalMemo = React.memo(TaskModal);

// Basic input selectors
const selectMyTask = (state) => state.myTask;

// Memoized selectors
export const selectPriorityFilter = createSelector([selectMyTask], (myTask) => myTask.priority);

export const selectTaskData = createSelector([selectMyTask], (myTask) => myTask.gridData);

export const selectZoneFilter = createSelector([selectMyTask], (myTask) => myTask.zone);

const TaskList = () => {
  const priorityFilter = useSelector(selectPriorityFilter);
  const taskData = useSelector(selectTaskData);
  const zoneFilter = useSelector(selectZoneFilter);

  const xMediaQuery = useMediaQuery('(max-width:450px)');
  const vMediaQuery = useMediaQuery('(max-width:900px)');
  const uMediaQuery = useMediaQuery('(max-width:1200px)');
  const aMediaQuery = useMediaQuery('(max-width:1450px)');
  const dispatch = useDispatch();

  const user = useSelector(
    createSelector(
      (state) => state.user,
      (user) => user
    )
  );
  const apiCallCounts = useSelector(
    createSelector(
      (state) => state.user,
      (user) => user.refreshApiCall
    )
  );

  const [myWorkData, setMyWorkData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [selectedTask, setSelectedTask] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isDrawerDataChange, setIsDrawerDataChange] = useState(false);
  const [filter, setFilter] = useState('');
  const [openTaskModal, setOpenTaskModal] = useState(false);

  const tAssignee = [...new Set(filteredData?.map((item) => item.Assignee))];
  const tSupervisor = [...new Set(filteredData?.map((item) => item.Supervisor))];
  const tTeamLead = [...new Set(filteredData?.map((item) => item.TeamLead))];
  const tCustomer = [...new Set(filteredData?.map((item) => item.CustomerName))];
  const tPriority = [...new Set(filteredData?.map((item) => item.T_Priority))];
  //const tCustomer = [...new Set(filteredData?.map((item) => item.FinishedDaysPercentage))];

  const chipsStyle = {
    '&.MuiChip-root': {
      height: '26px',
      fontSize: '12px',
      color: 'white',
      marginLeft: xMediaQuery ? '2px' : '6px',
      marginRight: xMediaQuery ? '2px' : '6px',
      width: xMediaQuery
        ? '76px'
        : vMediaQuery
        ? '100px'
        : uMediaQuery
        ? '94.4px'
        : aMediaQuery
        ? '111px'
        : '130px'
    }
  };

  const columns = [
    {
      field: 'T_Desc',
      headerName: 'Description',
      width: xMediaQuery ? 200 : 350,
      renderCell: (params) => <EllipsisTooltip text={params.value} />
    },
    {
      field: 'Assignee',
      headerName: 'Assignee',
      width: 150,
      filterOperators: [createMultiSelectOperator(tAssignee)]
    },
    {
      field: 'T_Reminder',
      headerName: 'Remainder',
      width: 90,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <div className="zone-chip">
          {params.value === 0 ? (
            <>-</>
          ) : (
            <Badge badgeContent={params.value} color="primary" overlap="circular" />
          )}
        </div>
      )
    },
    {
      field: 'FollowUpCount',
      headerName: 'Activity',
      width: 70,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Badge
          badgeContent={params.value}
          color="primary"
          overlap="circular"
          sx={{
            '& .MuiBadge-badge': {
              border: `1px solid ${theme.palette.primary.text} !important`
            }
          }}>
          <MapsUgcOutlinedIcon
            sx={
              params.value === 0
                ? {
                    fill: `#323338 !important`,
                    fontWeight: 200,
                    fontFamily: theme.palette.primary.font
                  }
                : {
                    '& path:nth-of-type(2)': {
                      visibility: 'hidden'
                    }
                  }
            }
          />
        </Badge>
      )
    },
    {
      field: 'RecentStatus',
      headerName: 'Status',
      width: 180,
      renderCell: (params) => <EllipsisTooltip text={params.value} />
    },
    {
      field: 'T_DueDate',
      headerName: 'Due Date',
      width: 110,
      valueGetter: (params) => (params.value ? moment(params.value).format('DD/MM/YYYY') : '')
    },
    {
      field: 'nextFollowUp',
      headerName: 'Next Due',
      width: 110,
      valueGetter: (params) => (params.value ? moment(params.value).format('DD/MM/YYYY') : '')
    },
    {
      field: 'FinishedDaysPercentage',
      headerName: 'Zone',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      filterOperators: [zoneOperator],
      renderCell: (params) => (
        <ChipLevel value={`${params.value}%`} color={getColor(params.value, zoneFilter, true)} />
      )
    },
    {
      field: 'T_Priority',
      headerName: 'Priority',
      width: 90,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <ChipLevel value={params.value} color={getColor(params.value, priorityFilter)} />
      ),
      filterOperators: [createMultiSelectOperator(tPriority)]
    },
    {
      field: 'CustomerName',
      headerName: 'Customer',
      width: 250,
      filterOperators: [createMultiSelectOperator(tCustomer)]
    },
    { field: 'T_ContactPerson', headerName: 'Contact Person', width: 150 },
    { field: 'T_ContactNo', headerName: 'Contact No', width: 130 },
    { field: 'T_Email', headerName: 'Email', width: 180 },
    { field: 'Department', headerName: 'Department', width: 110 },
    { field: 'ProjectName', headerName: 'Project', width: 150 },
    {
      field: 'Supervisor',
      headerName: 'Supervisor',
      width: 150,
      filterOperators: [createMultiSelectOperator(tSupervisor)]
    },
    {
      field: 'TeamLead',
      headerName: 'Team Lead',
      width: 150,
      filterOperators: [createMultiSelectOperator(tTeamLead)]
    },
    { field: 'LocName', headerName: 'Location', width: 90 },
    { field: 'CreatedBy', headerName: 'Created By', width: 150 },
    {
      field: 'CreatedDate',
      headerName: 'Created Date',
      width: 130,
      valueGetter: (params) => moment(params.value).format('DD/MM/YYYY')
    },
    {
      field: 'T_InformCust',
      headerName: 'Inform Customer',
      width: 140,
      valueGetter: (params) => (params.value === 'Y' ? 'Yes' : 'No')
    }
  ];

  const handleAddTaskOpen = useCallback(() => {
    setOpenTaskModal(true);
    setTimeout(() => {
      document.getElementById('taskTitle')?.focus();
    }, 500);
  }, []);

  const handleTaskModalClose = useCallback(() => setOpenTaskModal(false), []);

  const users = user?.filterData?.map((u) => u.UId).join(',') || '';

  const fMyWork = useCallback(async () => {
    const obj = {
      User_id: users,
      T_Status: 'PROGRESS'
    };
    const result = await pendingTaskAPI(obj);

    if (result.status === 200) {
      setMyWorkData(result?.data?.data);
      // Filter tasks that are in the "Red" zone
      const redZoneTasks = result?.data?.data?.filter(
        (item) => getZoneColor(item.FinishedDaysPercentage) === 'Red'
      );
      dispatch(setRedZones(redZoneTasks));

      dispatch(setZone(result?.data?.counts?.zone));
      dispatch(setPriority(result?.data?.counts?.priority));
      dispatch(setMyTaskGridData(result?.data?.data));
    }
  });

  useEffect(() => {
    const searchValue = filter.trim().toLowerCase();
    const filteredByAssignee = taskData.filter((task) => {
      const assignee = task.Assignee?.toLowerCase?.() || '';
      return assignee.includes(searchValue);
    });

    setFilteredData(filteredByAssignee);
  }, [filter, taskData]);

  const handleTaskClick = (task) => {
    setSelectedTask(task);

    setDrawerOpen(true);

    setTimeout(() => {
      document.getElementById('activity')?.focus();
    }, 500);
  };

  const handleDrawerClose = useCallback(() => {
    if (isDrawerDataChange) {
      fMyWork();
      setIsDrawerDataChange(true);
    }
    setDrawerOpen(false);
    setIsDrawerDataChange(false);
  });

  const getColor = (value, filter, isZone = false) => {
    if (isZone) {
      let zoneLabel = 'Green';
      if (value >= 90) zoneLabel = 'Red';
      else if (value >= 75) zoneLabel = 'Orange';
      else if (value >= 50) zoneLabel = 'Yellow';

      const foundZone = filter.find((item) => item.label === zoneLabel);
      return foundZone ? foundZone.color : '';
    }
    const foundItem = filter.find((item) => item.label === value);
    return foundItem ? foundItem.color : '';
  };

  // Calculating dynamic width based on chip count
  const getDynamicWidth = (chipCount) => {
    const minWidth = uMediaQuery ? '0px' : 100; // minimum width
    const widthPerChip = uMediaQuery ? 75 : 100; // width to add per chip
    return Math.min(minWidth + chipCount * widthPerChip, 600); // max width can be 600px
  };

  const handleExportExcel = () => {
    exportToExcel(myWorkData, 'myWork_data', 'Tasks');
  };

  const fModulesData = useCallback(async () => {
    const result = await getModuleQuickLaunchDataAPI();

    if (result.status === 200) {
      dispatch(setSftwrModules(result?.data?.data));
    }
  });

  useEffect(() => {
    fMyWork();
  }, [apiCallCounts?.myWorkRefreshCount, user?.filterData]);

  useEffect(() => {
    fModulesData();
  }, []);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <div className="search-field">
        <CmpTypographyHead xText={'My Work'} />
        <div className="exportDiv">
          <AnimatedSearchField
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
            }}
          />
          <Box className="exportBox">
            <IconButton onClick={handleExportExcel}>
              <FileDownloadOutlinedIcon />
            </IconButton>
            <Typography variant="caption" sx={{ mt: -1 }}>
              Export
            </Typography>
          </Box>
        </div>
      </div>
      <Grid container columnSpacing={0} sx={{ p: xMediaQuery ? '' : 1, pb: xMediaQuery ? 1 : '' }}>
        <Grid
          item
          xs={12}
          sm={2}
          md={1.7}
          lg={1}
          className={priorityFilter.length > 0 ? 'add-btn' : ''}>
          <CmpButton xbtnName={'Add Task'} xOnClick={handleAddTaskOpen} />
        </Grid>
        {/* Priority Filter Chips */}
        <Grid
          item
          xs={vMediaQuery ? 12 : 'auto'}
          sm={vMediaQuery ? 10 : 'auto'}
          className={priorityFilter.length > 0 ? 'chip' : ''}
          sx={{ width: getDynamicWidth(priorityFilter.length) }}>
          <Box
            sx={{
              mb: xMediaQuery ? '' : vMediaQuery ? 1 : '',
              mt: xMediaQuery ? 1 : '2px'
            }}>
            <Grid container spacing={0}>
              {priorityFilter.map((priority) => (
                <Grid item key={priority.label}>
                  <Badge
                    sx={badgeStyle}
                    badgeContent={priority.count}
                    color={priority.isSelected ? 'secondary' : 'primary'}
                    overlap="circular">
                    <Chip
                      className={priority.isSelected ? 'filterSupChip' : ''}
                      label={
                        <div className="chipalign">
                          {/* {priority.isSelected && (
                            <IconButton size="small">
                              <FilterAltOutlinedIcon fontSize="small" />
                            </IconButton>
                          )} */}

                          {priority.label}
                        </div>
                      }
                      sx={{
                        ...chipsStyle,
                        backgroundColor: priority.color,
                        '&:hover': {
                          backgroundColor: `${priority.color} !important`
                        }
                      }}
                    />
                  </Badge>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>
        {/* Zone Filter Chips */}
        <Grid
          item
          xs={vMediaQuery ? 12 : 'auto'}
          className="zone-chip"
          sx={{ width: getDynamicWidth(zoneFilter.length) }}>
          <Box
            sx={{
              mt: xMediaQuery ? 1 : '2px'
            }}>
            <Grid container spacing={0}>
              {zoneFilter.map((zone) => (
                <Grid item key={zone.label}>
                  <Badge
                    sx={badgeStyle}
                    badgeContent={zone.count}
                    color={zone.isSelected ? 'secondary' : 'primary'}>
                    <Chip
                      className={zone.isSelected ? 'filterSupChip' : ''}
                      label={
                        <div className="chipalign">
                          {/* {zone.isSelected && (
                            <IconButton size="small">
                              <FilterAltOutlinedIcon fontSize="small" />
                            </IconButton>
                          )} */}

                          {zone.label}
                        </div>
                      }
                      sx={{
                        ...chipsStyle,
                        backgroundColor: zone.color,
                        '&:hover': {
                          backgroundColor: `${zone.color} !important`
                        }
                      }}
                    />
                  </Badge>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>
      </Grid>

      <DataGridPro
        className="gridTable"
        sx={{
          height: '55vh',
          '& .MuiDataGrid-row:hover': {
            backgroundColor: '#c4c4c40f'
          }
        }}
        rowHeight={35}
        headerHeight={40}
        rows={filteredData.length > 0 ? filteredData : []}
        columns={columns}
        disableSelectionOnClick
        getRowId={(row) => row.T_ID}
        onRowClick={(params) => handleTaskClick(params.row)}
        hideFooter
        initialState={{
          pinnedColumns: !xMediaQuery ? { left: ['T_Desc'] } : {}
        }}
      />

      {openTaskModal && (
        <Suspense fallback={<p className={'loading'}>Loading...</p>}>
          <CmpModal
            open={openTaskModal}
            onClose={handleTaskModalClose}
            xMediaQuery={xMediaQuery}
            vMediaQuery={vMediaQuery}
            width={vMediaQuery ? '95%' : '70%'}
            height={vMediaQuery ? '80vh' : 'auto'}
            title="Create Task">
            <TaskModalMemo open={openTaskModal} handleClose={handleTaskModalClose} />
          </CmpModal>
        </Suspense>
      )}

      {drawerOpen && (
        <Suspense fallback={<p>Loading...</p>}>
          <UpdateFollowUpDrawerMemo
            drawerOpen={drawerOpen}
            handleDrawerClose={handleDrawerClose}
            selectedTask={selectedTask}
            filteredGridData={filteredData}
            setFilteredGridData={setFilteredData}
            setSelectedTask={setSelectedTask}
            xMediaQuery={xMediaQuery}
            apiCallCounts={apiCallCounts}
            fMyWork={fMyWork}
            setIsDrawerDataChange={setIsDrawerDataChange}
            heading={'My Work'}
          />
        </Suspense>
      )}
    </Box>
  );
};

export default TaskList;
